/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import groupBy from 'lodash/groupBy';
import React, { memo, useEffect, useMemo, useState } from 'react';
import Countdown, { CountdownRendererFn, zeroPad } from 'react-countdown';
import { useSelector } from 'react-redux';

import {
  HorseColor,
  horseColorIndex,
  selectConnected,
  selectCurrentState,
  selectDebugSpawnSource,
  selectGameStartDate,
  selectGameType,
  selectHasGameplayStarted,
  selectHorseAmount,
  selectHorses,
  selectIsGameRunning,
  selectIsPreGameState,
  selectLastCard,
  selectLastCardHorseIndex,
  selectLastRoundFinished,
  selectLastTurnDicePlayerId,
  selectLastTurnDices,
  selectLastTurnDicesCur,
  selectPlayerId,
  selectPlayers,
  selectRestoreState,
  selectRound,
  selectTurnDicesPlayerId,
  selectTurnOrder,
  selectTurnPos,
  selectTurnType,
  useTranslation,
} from '~/app';
import { Horse } from '~/board';
import CountryFlagTooltip from '~/components/CountryFlagTooltip';
import PlayerProfilePicture from '~/components/ProfilePicture/PlayerProfilePicture';
import { presets, TextTransition } from '~/components/TextTransition';
import WaitingIndicator from '~/components/WaitingIndicator/WaitingIndicator';
import { CardValue } from '~/overlay/bar-bottom/Card';
import Dice from '~/overlay/turn-controls/Dice';
import { GameType } from '~/server/src/dtos';
import Turfmaster from '~/server/src/tmmodel';
import { cn } from '~/utils/styles';

import BarTopHorse from './BarTopHorse';
import FloatingMenu, { FloatingMenuItemKey } from './FloatingMenu';
import LastCard from './LastCard';

const countdownRenderer: CountdownRendererFn = ({ hours, minutes, seconds }) => {
  return (
    <span className="align-middle font-mono font-bold">
      {zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}
    </span>
  );
};

/** Matches typical debug/free lobby layout so status text does not shift on connect. */
const PLACEHOLDER_PLAYER_COUNT = 2;
const PLACEHOLDER_HORSES_PER_PLAYER = 2;

const playerRowLayoutClassName =
  'xs:gap-2 mb-1 flex w-full flex-row items-end justify-center gap-1 sm:gap-3 md:mb-2 md:gap-6 lg:gap-10 xl:gap-14';

const PlayerRowPlaceholder = (): JSX.Element => (
  <div className={playerRowLayoutClassName} aria-hidden>
    {Array.from({ length: PLACEHOLDER_PLAYER_COUNT }).map((_, playerIndex) => (
      <div key={playerIndex} className="flex flex-col items-stretch justify-end">
        <div className="xs:space-x-1.5 z-20 flex flex-row items-center justify-center space-x-1 px-2 drop-shadow-md sm:space-x-2 md:space-x-3">
          {Array.from({ length: PLACEHOLDER_HORSES_PER_PLAYER }).map((_, horseIndex) => (
            <div
              key={horseIndex}
              className="h-8 w-8 shrink-0 rounded-md border border-dashed border-white/25 bg-white/5 sm:h-10 sm:w-10 md:h-12 md:w-12"
            />
          ))}
        </div>
        <div className="pointer-events-none z-10 -mt-5 flex w-32 flex-col items-center justify-end rounded-xl border border-dashed border-white/25 bg-white/10 pt-6">
          <div className="h-5 w-20 rounded-sm bg-white/5" />
        </div>
      </div>
    ))}
  </div>
);

interface BarTopProps {
  onFloatingMenuItemClicked: (key: FloatingMenuItemKey, event?: Event) => void;
  isDiceShuffling: boolean;
  isDiceSelectionReady: boolean;
  /** Dice round only: last-card should linger after shuffle until server confirms selection or fallback expires. */
  isLastCardDiceLingerExpired: boolean;
  isSpectatorMode?: boolean;
  isHistoryMode?: boolean;
}

const BarTop = ({
  onFloatingMenuItemClicked,
  isDiceShuffling,
  isLastCardDiceLingerExpired,
  isSpectatorMode = false,
  isHistoryMode = false,
}: BarTopProps) => {
  const { t } = useTranslation();

  const horseAmount = useSelector(selectHorseAmount);
  const horses = useSelector(selectHorses);
  const players = useSelector(selectPlayers);
  const playerId = useSelector(selectPlayerId);
  const gameType = useSelector(selectGameType);
  const gameStartDate = useSelector(selectGameStartDate);
  const currentState = useSelector(selectCurrentState);
  const round = useSelector(selectRound);
  const turnOrder = useSelector(selectTurnOrder);
  const turnDicePlayerId = useSelector(selectTurnDicesPlayerId);
  const turnPos = useSelector(selectTurnPos);
  const turnType = useSelector(selectTurnType);
  const lastCardHorseId = useSelector(selectLastCardHorseIndex);
  const lastCard = useSelector(selectLastCard);
  const lastTurnDicePlayerId = useSelector(selectLastTurnDicePlayerId);
  const lastTurnDicePlayerName =
    lastTurnDicePlayerId >= 0 && players[lastTurnDicePlayerId]
      ? players[lastTurnDicePlayerId].name
      : '';
  const lastTurnDices = useSelector(selectLastTurnDices);
  const lastTurnDicesCur = useSelector(selectLastTurnDicesCur);
  const isGameRunning = useSelector(selectIsGameRunning);
  const isPreGameState = useSelector(selectIsPreGameState);
  const hasGameplayStarted = useSelector(selectHasGameplayStarted);
  const connected = useSelector(selectConnected);
  const restoreState = useSelector(selectRestoreState);
  const debugSpawnSource = useSelector(selectDebugSpawnSource);

  const currentHorseIndex = turnOrder[turnPos];
  const currentHorse = horses[currentHorseIndex];
  const currentPlayerId = currentHorse ? currentHorse.playerId : -1;
  const currentPlayer = currentPlayerId >= 0 ? players[currentHorse.playerId] : null;
  const currentPlayerName = currentPlayer ? players[currentHorse.playerId].name : '';
  const currentTurnThrowDicePlayerName =
    turnDicePlayerId >= 0 && players[turnDicePlayerId] ? players[turnDicePlayerId].name : '';
  const lastRoundFinished = useSelector(selectLastRoundFinished);

  const lastCardColor = horses && horses[lastCardHorseId] && horses[lastCardHorseId].color;

  const horsesGroupedByPlayer: Horse[][] = useMemo(
    () => Object.values(groupBy(horses, 'playerId')),
    [horses],
  );
  const horsesPerPlayer = useMemo(
    () => (horsesGroupedByPlayer && horsesGroupedByPlayer[0] ? horsesGroupedByPlayer[0].length : 0),
    [horsesGroupedByPlayer],
  );
  const playerCount = useMemo(() => horseAmount / horsesPerPlayer, [horseAmount, horsesPerPlayer]);
  const lastDiceColor =
    horsesGroupedByPlayer &&
    horsesGroupedByPlayer.length >= lastTurnDicePlayerId + 1 &&
    horsesGroupedByPlayer[lastTurnDicePlayerId] &&
    horsesGroupedByPlayer[lastTurnDicePlayerId].length > 0 &&
    horsesGroupedByPlayer[lastTurnDicePlayerId][0].color;

  const setupPreviewHorses: Horse[][] = useMemo(
    () =>
      Array.from(
        // how many players are missing / have to join?
        { length: playerCount - horses.length / horsesPerPlayer },
        (_v, k) => k,
      ).map<Horse[]>(() =>
        // give the missing players mocked horses with the same amount every player gets
        Array.from({ length: horsesPerPlayer }, (_v, k) => k).map(() => ({
          index: -1,
          playerId: -1,
          color: horseColorIndex.BLACK,
          jockeyName: '',
          realColor: horseColorIndex.BLACK,
          name: '',
        })),
      ),
    [horses.length, horsesPerPlayer, playerCount],
  );

  // determine status in top bar
  const statusRound = useMemo(() => {
    return t('game_statusRoundNumber', {
      placeholders: { round: Math.min(round + 1, Turfmaster.rounds) },
    });
  }, [round, t]);
  const statusTurnType = useMemo(() => {
    return turnType === Turfmaster.turntypes.CARDS
      ? t('game_statusRoundTypeCards')
      : t('game_statusRoundTypeDices');
  }, [t, turnType]);
  const statusTextLeft = lastRoundFinished ? '' : `${statusRound} | ${statusTurnType}`;
  const statusTextRight =
    lastTurnDicePlayerName && lastTurnDicePlayerName.length
      ? t('game_statusLastRoll', { placeholders: { playerName: lastTurnDicePlayerName } })
      : '';
  const { statusText, isTournamentCountdown, isWaitingForGameStart } = useMemo<{
    statusText: string;
    isTournamentCountdown?: boolean;
    isWaitingForGameStart?: boolean;
  }>(() => {
    if (horses && horses.length > 0) {
      // select a card or a dice / also shown when bonus card was selected
      if (
        currentState === Turfmaster.stateMachine.SELECTCARDDICE ||
        currentState === Turfmaster.stateMachine.ADDBONUSCARDSOPP
      ) {
        // select a card
        if (turnType === Turfmaster.turntypes.CARDS) {
          if (!isSpectatorMode && currentPlayerId === playerId)
            return { statusText: t('game_statusSelectCardSelf') };
          else if (currentPlayerName && currentPlayerName.length > 0)
            return {
              statusText: t('game_statusSelectCardOpponent', {
                placeholders: { playerName: currentPlayerName },
              }),
            };
        }
        // select one or both dice(s)
        else {
          if (!isSpectatorMode && currentPlayerId === playerId)
            return { statusText: t('game_statusSelectDiceSelf') };
          else if (currentPlayerName && currentPlayerName.length > 0)
            return {
              statusText: t('game_statusSelectDiceOpponent', {
                placeholders: { playerName: currentPlayerName },
              }),
            };
        }
      }
      // select a move
      else if (currentState === Turfmaster.stateMachine.SELECTMOVE) {
        // select a card
        if (turnType === Turfmaster.turntypes.CARDS) {
          if (!isSpectatorMode && currentPlayerId === playerId)
            return { statusText: t('game_statusSelectCardSelf') };
          else if (currentPlayerName && currentPlayerName.length > 0)
            return {
              statusText: t('game_statusSelectMoveOpponent', {
                placeholders: { playerName: currentPlayerName },
              }),
            };
        }
        // select one or both dice(s)
        else {
          if (!isSpectatorMode && currentPlayerId === playerId)
            return { statusText: t('game_statusSelectMoveWithDicesSelf') };
          else if (currentPlayerName && currentPlayerName.length > 0)
            return {
              statusText: t('game_statusSelectMoveOpponent', {
                placeholders: { playerName: currentPlayerName },
              }),
            };
        }
      }
      // throw a dice
      else if (currentState === Turfmaster.stateMachine.SELECTDICES) {
        if (!isSpectatorMode && !isDiceShuffling && turnDicePlayerId === playerId)
          return { statusText: t('game_statusThrowDicesSelf') };
        else if (currentTurnThrowDicePlayerName && currentTurnThrowDicePlayerName.length > 0)
          return {
            statusText: t('game_statusSelectMoveOpponent', {
              placeholders: { playerName: currentTurnThrowDicePlayerName },
            }),
          };
      }
      // merge cards
      else if (currentState === Turfmaster.stateMachine.MERGECARDS) {
        if (!isSpectatorMode && currentPlayerId === playerId)
          return { statusText: t('game_statusMergeCardsSelf') };
        else if (currentPlayerName && currentPlayerName.length > 0)
          return {
            statusText: t('game_statusMergeCardsOpponent', {
              placeholders: { playerName: currentPlayerName },
            }),
          };
      }
      // If gameplay has started but we don't have a specific status message yet,
      // prefer showing nothing over a generic fallback (prevents "Rennen läuft" flicker).
      if (hasGameplayStarted) return { statusText: '' };
    }

    if (round === 0 && isPreGameState) {
      switch (gameType) {
        case GameType.Training:
          return {
            statusText: t('game_statusWait_training'),
            isTournamentCountdown: false,
            isWaitingForGameStart: true,
          };
        case GameType.Free:
          return {
            statusText: t('game_statusWait_free', {
              placeholders: { playerCount: playerCount - players.length },
              pluralCount: playerCount - players.length,
            }),
            isWaitingForGameStart: true,
          };
        case GameType.TournamentBronze:
        case GameType.TournamentSilver:
        case GameType.TournamentGold:
          return {
            statusText: gameStartDate ? '' : t('game_statusWait_default'),
            isTournamentCountdown: !!gameStartDate,
            isWaitingForGameStart: !gameStartDate,
          };
        default:
          return {
            statusText: t('game_statusWait_default'),
            isWaitingForGameStart: true,
          };
      }
    }

    // make sure status is at least an empty string
    return { statusText: '' };
  }, [
    horses,
    round,
    currentState,
    turnType,
    currentPlayerId,
    playerId,
    t,
    currentPlayerName,
    isDiceShuffling,
    isSpectatorMode,
    turnDicePlayerId,
    currentTurnThrowDicePlayerName,
    gameType,
    playerCount,
    players.length,
    gameStartDate,
    isPreGameState,
    hasGameplayStarted,
  ]);

  const [lastNonEmptyStatusText, setLastNonEmptyStatusText] = useState('');
  useEffect(() => {
    if (isTournamentCountdown) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastNonEmptyStatusText('');
      return;
    }
    if (statusText) {
      setLastNonEmptyStatusText(statusText);
    } else if (!hasGameplayStarted && isPreGameState) {
      setLastNonEmptyStatusText('');
    }
  }, [hasGameplayStarted, isPreGameState, isTournamentCountdown, statusText]);

  const displayedStatusText = isTournamentCountdown
    ? statusText
    : statusText || (hasGameplayStarted ? '' : lastNonEmptyStatusText);

  const isDebugSpawnConnecting =
    debugSpawnSource != null && !hasGameplayStarted && restoreState !== 'error';

  const showGameStartLoading =
    isWaitingForGameStart &&
    !!displayedStatusText &&
    !hasGameplayStarted &&
    (!connected || restoreState === 'loading' || isDebugSpawnConnecting);

  const lastDices = useMemo(() => {
    return lastTurnDices.map((value, index) => {
      // 1. 'invalid' selection at startup should always be visible
      // 2. visible if all dices are selected
      // 3. visible if this specific dice is selected
      return { value, selected: value < 0 || lastTurnDicesCur > 1 || index === lastTurnDicesCur };
    });
  }, [lastTurnDices, lastTurnDicesCur]);

  const [nowTime, setNowTime] = useState(() => new Date().getTime());

  useEffect(() => {
    if (!isTournamentCountdown) return;

    const interval = setInterval(() => setNowTime(new Date().getTime()), 1000);
    return () => clearInterval(interval);
  }, [isTournamentCountdown]);

  const shouldShowLastCard = useMemo(() => {
    const hasLastCard =
      lastCardHorseId >= 0 &&
      (lastCard as CardValue) !== undefined &&
      typeof lastCard === 'number' &&
      lastCard !== -1;

    if (!hasLastCard) return false;

    if (turnType === Turfmaster.turntypes.CARDS) return true;

    // Dice round: show last played card during dice shuffle + short linger window afterwards.
    // Hide once server confirms dice selection (SELECTDICESOPP) or fallback timer expires.
    return (
      turnType === Turfmaster.turntypes.DICES &&
      currentState === Turfmaster.stateMachine.SELECTDICES &&
      !isLastCardDiceLingerExpired
    );
  }, [currentState, isLastCardDiceLingerExpired, lastCard, lastCardHorseId, turnType]);

  return (
    <div
      className={cn(
        'pointer-events-auto absolute top-0 right-0 left-0 flex h-[15%] w-screen flex-row items-center rounded-b-lg border-x border-b border-black/50 bg-[#00000055] backface-hidden',
        isSpectatorMode && 'shadow-[inset_0_2px_0_0_rgb(244_63_94/0.5)]',
        isHistoryMode && 'shadow-[inset_0_2px_0_0_rgb(245_158_11/0.5)]',
      )}
    >
      <FloatingMenu className="absolute top-4 left-4" onItemClicked={onFloatingMenuItemClicked} />

      {/* Center section: column layout for horse icons+badges and status text */}
      <div
        className="mr-4 flex h-full flex-1 flex-col items-center justify-center px-2"
        style={{ marginLeft: 'calc(2.5rem + 2rem)' }}
      >
        {/* Player row: placeholder reserves height while snapshot/game connects */}
        {showGameStartLoading ? (
          <PlayerRowPlaceholder />
        ) : (
          <div className={playerRowLayoutClassName}>
          {horsesGroupedByPlayer.concat(setupPreviewHorses).map((horsesOfPlayer, playerIndex) => {
            // Determine if this is a real player or a waiting slot
            const isWaiting =
              horsesOfPlayer[0]?.playerId === -1 || !players[horsesOfPlayer[0]?.playerId];
            const playerName = !isWaiting ? players[horsesOfPlayer[0]?.playerId]?.name || '' : '';
            const badgeText = !isWaiting ? playerName : t('game_playerSlot_waiting');
            const playerCountry = !isWaiting
              ? players[horsesOfPlayer[0]?.playerId]?.country
              : undefined;
            const playerUserId = !isWaiting
              ? players[horsesOfPlayer[0]?.playerId]?.userId
              : undefined;
            const isActivePlayer =
              !isWaiting &&
              horsesOfPlayer.some((horse) => horse.playerId === currentPlayerId) &&
              hasGameplayStarted;

            return (
              <div key={playerIndex} className="flex flex-col items-stretch justify-end">
                {/* Horse icons row, perfectly centered above badge */}
                <div className="xs:space-x-1.5 z-20 flex flex-row items-center justify-center space-x-1 px-2 drop-shadow-md sm:space-x-2 md:space-x-3">
                  {horsesOfPlayer.map((horse, horseIndexOfPlayer) => {
                    return (
                      <BarTopHorse
                        key={`${horseIndexOfPlayer} ${playerIndex}`}
                        horseIndexOfPlayer={horseIndexOfPlayer}
                        horseIndex={horses.indexOf(horse)}
                        preview={horse.index === -1}
                        className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12"
                      />
                    );
                  })}
                </div>
                {/* Player name badge below icons, half behind (overlap), always rendered */}
                <div
                  className={cn(
                    'pointer-events-none z-10 -mt-5 flex flex-col items-center justify-end rounded-xl border border-black/50 pt-6 transition-all duration-300',
                    isActivePlayer ? 'bg-black/40 ring-2 ring-yellow-300/40' : 'bg-black/20',
                    isWaiting && 'animate-pulse',
                  )}
                >
                  <div className="relative z-10 flex w-32 items-center justify-center px-2 py-1 text-xs text-white sm:text-[11px]">
                    <div className="flex w-full min-w-0 items-center justify-center gap-2">
                      {/* Profile picture with flag badge */}
                      {!isWaiting && playerUserId ? (
                        <div className="relative shrink-0">
                          <PlayerProfilePicture
                            userId={playerUserId}
                            username={playerName}
                            size="sm"
                            className="h-4 w-4"
                          />
                          {/* Country flag badge at bottom right */}
                          {playerCountry && (
                            <div className="absolute -right-1 -bottom-1 z-20">
                              <CountryFlagTooltip countryCode={playerCountry} size="sm" />
                            </div>
                          )}
                        </div>
                      ) : isWaiting ? (
                        <WaitingIndicator size="sm" className="text-white" />
                      ) : (
                        <div className="h-5 w-5 shrink-0" />
                      )}

                      <span
                        className={cn(
                          'min-w-0 overflow-hidden text-center text-ellipsis whitespace-nowrap',
                          isWaiting && 'animate-pulse',
                        )}
                      >
                        {badgeText}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}

        {/* Absolutely positioned left status text */}
        {isGameRunning && (
          <div className="pointer-events-none absolute bottom-2 left-6 z-30 text-[2vh] leading-[1.5vh] whitespace-nowrap text-white">
            {statusTextLeft}
          </div>
        )}

        {/* Absolutely positioned right status text */}
        {isGameRunning && (
          <div className="pointer-events-none absolute right-6 bottom-2 z-30 text-[2vh] leading-[2vh] whitespace-nowrap text-white">
            {statusTextRight}
          </div>
        )}

        {/* Centered status text row (keep only the center part here) */}
        <div className="flex w-full flex-row items-end justify-center text-center text-white">
          <div className="flex flex-row items-center justify-center gap-2">
            {showGameStartLoading && (
              <WaitingIndicator size="lg" className="shrink-0 text-white" />
            )}
            <TextTransition
              springConfig={presets.gentle}
              transitionKey={displayedStatusText}
              className={cn('text-center text-clip whitespace-nowrap', {
                'opacity-0': isTournamentCountdown,
              })}
            >
              <div className="text-[1.5vh] leading-[3vh] md:text-[2vh] lg:text-[2.5vh]">
                {displayedStatusText}
              </div>
            </TextTransition>
          </div>
          {isTournamentCountdown && gameStartDate && (
            <div className="flex flex-row items-center space-x-2 overflow-hidden text-center align-middle text-[3vh] leading-[3vh] text-clip whitespace-nowrap">
              <span className="align-middle">{t('game_statusWait_tournament')}</span>
              <Countdown
                key={`${nowTime}_${gameStartDate}`}
                date={gameStartDate}
                renderer={countdownRenderer}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right section: dice/cards */}
      {isGameRunning && (
        <div className="flex h-[11.5vh] items-center self-start">
          {shouldShowLastCard && (
            <LastCard
              data-testid="last-card"
              value={lastCard as CardValue}
              className={cn('h-[80%] w-auto pr-[8px] transition-all', {
                'pr-[4vw]': !lastDices || lastDices.length <= 0,
              })}
              color={lastCardColor}
            />
          )}
          {lastDices.map((diceObject, index) => (
            <Dice
              key={index}
              value={diceObject.value}
              color={lastDiceColor as HorseColor}
              disabled={!diceObject.selected}
              className={cn('h-1/2 w-auto pr-[8px] transition-all', {
                'pr-[4vw]': index >= lastDices.length - 1,
              })}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(BarTop);
